import { CollectionReference, DocumentReference } from "@firebase/firestore-types";
import { processFile, TIMEOUT, uploadFile } from "src/global";
import { importAPSStatementData } from "./APSStatementImporter";

// Imports a list of PDF files all at once.
// Uses the APStatementImporter.tsx file for shared code.

export function importAPSStatements(
  projectId: string,
  projectDocument: DocumentReference,
  statementsCollection: CollectionReference,
  pdfFiles: FileList
): Promise<any> {
  return new Promise<any>(resolve => {
    const promises: any[] = [];
    for (let i = 0; i < pdfFiles.length; i++) {
      const pdfFile = pdfFiles.item(i);
      if (pdfFile) {
        promises.push(
          processFile(pdfFile).then(pdfPages => {
            if (pdfPages) {
              statementsCollection
                .add({
                  statementDate: Date.now(),
                  serviceStartDae: Date.now(),
                  serviceEndDate: Date.now(),
                  totalAmountDue: 0,
                  uploaded: false,
                  storagePath: "",
                  filename: "",
                  yearsWithSolar: 0,
                  creditBalance: 0,
                })
                .then(statementDoc => {
                  const advancedStatementDocument = projectDocument
                    .collection("advancedDualRateStatements")
                    .doc(statementDoc.id);
                  const simpleStatementDocument = projectDocument
                    .collection("simpleDualRateStatements")
                    .doc(statementDoc.id);
                  advancedStatementDocument
                    .get()
                    .then(doc => {
                      if (!doc.exists) {
                        advancedStatementDocument.set({
                          gridReadDate: Date.now(),
                          gridReading: 0,
                          priorGridReadDate: Date.now(),
                          priorGridReading: 0,
                          onPeakGridReadDate: Date.now(),
                          onPeakGridReading: 0,
                          priorOnPeakGridReadDate: Date.now(),
                          priorOnPeakGridReading: 0,
                          solarReadDate: Date.now(),
                          solarReading: 0,
                          priorSolarReadDate: Date.now(),
                          priorSolarReading: 0,
                          onPeakSolarReadDate: Date.now(),
                          onPeakSolarReading: 0,
                          priorOnPeakSolarReadDate: Date.now(),
                          priorOnPeakSolarReading: 0,
                          solarGeneratedReadDate: Date.now(),
                          solarGeneratedReading: 0,
                          priorSolarGeneratedReadDate: Date.now(),
                          priorSolarGeneratedReading: 0,
                          onPeakCredits: 0,
                          offPeakCredits: 0,
                          netElectricityCredit: 0,
                        });
                      }
                    })
                    .then(() => {
                      simpleStatementDocument.get().then(simpleDoc => {
                        if (!simpleDoc.exists) {
                          simpleStatementDocument.set({
                            totalElectricityFromGrid: 0,
                            onPeakElectricityFromGrid: 0,
                            totalElectricityCredited: 0,
                            onPeakElectricityCredited: 0,
                            lastMonthOnPeakCredit: 0,
                            lastMonthOffPeakCredit: 0,
                            solarGenerated: 0,
                          });
                        }
                      });
                    })
                    .then(() => {
                      const serviceChargesCollection = advancedStatementDocument.collection(
                        "serviceCharges"
                      );
                      const taxesAndFeesCollection = advancedStatementDocument.collection(
                        "taxesAndFees"
                      );
                      const {
                        statementDate,
                        priorGridReadDate,
                        gridReadDate,
                        gridReading,
                        priorGridReading,
                        solarReadDate,
                        solarReading,
                        priorSolarReadDate,
                        priorSolarReading,
                        onPeakGridReadDate,
                        onPeakGridReading,
                        priorOnPeakGridReadDate,
                        priorOnPeakGridReading,
                        onPeakSolarReadDate,
                        onPeakSolarReading,
                        priorOnPeakSolarReadDate,
                        priorOnPeakSolarReading,
                        solarGeneratedReadDate,
                        onPeakCredits,
                        offPeakCredits,
                        netElectricityCredit,
                        totalServiceCharges,
                        totalTaxesAndFees,
                      } = importAPSStatementData(
                        undefined,
                        pdfPages,
                        Date.now(),
                        0,
                        Date.now(),
                        0,
                        Date.now(),
                        0,
                        Date.now(),
                        0,
                        serviceChargesCollection,
                        taxesAndFeesCollection
                      );

                      const serviceStartDate = priorGridReadDate;
                      const serviceEndDate = gridReadDate;

                      statementDoc
                        .update({
                          statementDate,
                          serviceStartDate,
                          serviceEndDate,
                          yearsWithSolar: 0,
                          totalAmountDue: totalServiceCharges + totalTaxesAndFees,
                          storagePath: "",
                          filename: "",
                          uploaded: false,
                          notes: "",
                        })
                        .then(() => {
                          advancedStatementDocument.update({
                            gridReadDate,
                            gridReading,
                            priorGridReadDate,
                            priorGridReading,
                            onPeakGridReadDate,
                            onPeakGridReading,
                            priorOnPeakGridReadDate,
                            priorOnPeakGridReading,
                            solarReadDate,
                            solarReading,
                            priorSolarReadDate,
                            priorSolarReading,
                            onPeakSolarReadDate,
                            onPeakSolarReading,
                            priorOnPeakSolarReadDate,
                            priorOnPeakSolarReading,
                            solarGeneratedReadDate,
                            onPeakCredits,
                            offPeakCredits,
                            netElectricityCredit,
                          });
                        })
                        .then(() => {
                          simpleStatementDocument.update({
                            totalElectricityFromGrid: gridReading - priorGridReading,
                            onPeakElectricityFromGrid: onPeakGridReading - priorOnPeakGridReading,
                            totalElectricityCredited: solarReading - priorSolarReading,
                            onPeakElectricityCredited: onPeakSolarReading - priorOnPeakSolarReading,
                            lastMonthOnPeakCredit: onPeakCredits,
                            lastMonthOffPeakCredit: offPeakCredits,
                            solarGenerated: 0,
                          });
                        })
                        .then(() => {
                          setTimeout(() => {
                            uploadFile(
                              projectId,
                              statementDoc.id,
                              pdfFile,
                              uploadProgress,
                              false
                            ).then(file => {
                              if (!file) {
                                const storagePath = `projects/${projectId}/${statementDoc.id}.pdf`;
                                statementDoc.update({
                                  uploaded: true,
                                  storagePath,
                                  filename: pdfFile.name,
                                });
                              }
                            });
                          }, TIMEOUT);
                        });
                    });
                });
            }
          })
        );
      } // if(pdfFile)
    }
    Promise.all(promises).then(() => {
      resolve(projectId);
    });
  });
}

function uploadProgress(enabled: boolean, min: number, max: number, value: number) {
  return;
}



// WEBPACK FOOTER //
// ./src/pages/projects/project/statements/advanced/dualrate/APSStatementsImporter.tsx